// =========================================================================
// React-free core: initAnalytics + trackPageview + installRouteTrackerVanilla.
//
// Everything in this file MUST stay React-free. The package's React hook
// imports from this module, never the other way around, so a static-HTML
// or Astro caller never pulls react into their bundle.
// =========================================================================

export type AnalyticsApp =
  | 'marketing'
  | 'docs'
  | 'marketplace'
  | 'shop'
  | 'sell'
  | 'web'
  | 'admin';

export interface AnalyticsInitOpts {
  /** App identifier — must match the analytics.pageviews CHECK constraint. */
  app: AnalyticsApp;
  /**
   * Full URL of the track-pageview ingest endpoint. Defaults to
   * `${VITE_SUPABASE_URL}/functions/v1/track-pageview` if SUPABASE_URL is
   * passed; otherwise the caller MUST set this explicitly.
   */
  endpoint?: string;
  /**
   * Convenience shorthand. When passed (without a trailing slash) the
   * endpoint defaults to `${supabaseUrl}/functions/v1/track-pageview`.
   */
  supabaseUrl?: string;
  /** Public anon / publishable key — required for the API gateway. */
  anonKey: string;
  /**
   * Optional: when present we call it before every pageview and pass the
   * returned access_token in the Authorization header. Failures are
   * silently treated as anonymous; the page render is the contract.
   */
  getSession?: () => Promise<{ access_token: string } | null> | { access_token: string } | null;
  /**
   * Override debounce window in ms. Defaults to 500 — guards against
   * React StrictMode double-effects and rapid route swaps.
   */
  debounceMs?: number;
}

export interface TrackPageviewOpts {
  path_template?: string;
  vendor_slug?: string;
  product_id?: string;
}

export interface AnalyticsClient {
  trackPageview(opts?: TrackPageviewOpts): void;
}

const SESSION_KEY = 'pc_analytics_sid';
const RUO_ACK_KEY = 'pc_ruo_ack_v1';

// Module-scoped state. There's only one ingest endpoint per page so a
// singleton is fine — second initAnalytics() calls override the config.
interface State {
  app: AnalyticsApp;
  endpoint: string;
  anonKey: string;
  getSession: AnalyticsInitOpts['getSession'];
  debounceMs: number;
  lastFiredAt: number;
  lastFiredKey: string;
}
let state: State | null = null;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function safeSessionStorage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  if (!isBrowser()) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function randomUuid(): string {
  if (isBrowser() && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  // RFC4122-ish fallback. We only need uniqueness within a single
  // sessionStorage scope so a Math.random() variant is fine.
  const tpl = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return tpl.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateSessionId(): string {
  const ss = safeSessionStorage();
  if (!ss) return randomUuid();
  const existing = ss.getItem(SESSION_KEY);
  if (existing && /^[0-9a-fA-F-]{36}$/.test(existing)) return existing;
  const fresh = randomUuid();
  try {
    ss.setItem(SESSION_KEY, fresh);
  } catch {
    // Quota / privacy mode. Track once per page load instead.
  }
  return fresh;
}

/**
 * Returns true when the tracker should NOT fire. Centralizes every
 * opt-out: DNT, browser private mode, RUO modal on the shop public
 * storefront, missing globals.
 */
export function shouldSuppressTracking(pathname: string): boolean {
  if (!isBrowser()) return true;
  // Browser-level opt out — same flag GA respects.
  if (navigator.doNotTrack === '1' || (navigator as { msDoNotTrack?: string }).msDoNotTrack === '1') {
    return true;
  }
  // RUO attestation gate (Worker A's modal in apps/shop). Until the user
  // acknowledges, every storefront page is masked by a blocking modal —
  // firing a pageview would inflate engagement numbers.
  if (pathname.startsWith('/@')) {
    const ls = safeLocalStorage();
    if (ls && ls.getItem(RUO_ACK_KEY) == null) return true;
  }
  return false;
}

function parseUtm(search: string): Record<string, string> {
  const utm: Record<string, string> = {};
  if (!search) return utm;
  try {
    const params = new URLSearchParams(search);
    for (const key of ['source', 'medium', 'campaign', 'term', 'content']) {
      const v = params.get(`utm_${key}`);
      if (v) utm[key] = v.slice(0, 128);
    }
  } catch {
    // ignore — malformed search string is rare and not worth blowing up
  }
  return utm;
}

/**
 * Normalize a path so dashboard rollups group analogous URLs together.
 * UUID / hex id segments become `:id`; numeric ids become `:n`;
 * everything else stays put. Slug-style segments (`@vendor`, `kebab-case`)
 * preserve since they're meaningful.
 */
export function normalizePath(pathname: string): string {
  if (!pathname) return '/';
  const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const HEX24_RE = /^[0-9a-fA-F]{24,}$/;
  const NUM_RE = /^[0-9]+$/;
  return (
    '/' +
    pathname
      .split('/')
      .filter(Boolean)
      .map((seg) => {
        if (UUID_RE.test(seg)) return ':id';
        if (HEX24_RE.test(seg)) return ':id';
        if (NUM_RE.test(seg)) return ':n';
        return seg;
      })
      .join('/')
  );
}

/**
 * Idempotent init. Subsequent calls override the prior config (useful
 * for HMR). Returns an interface with trackPageview() bound to the
 * latest config.
 */
export function initAnalytics(opts: AnalyticsInitOpts): AnalyticsClient {
  const endpoint =
    opts.endpoint ??
    (opts.supabaseUrl
      ? opts.supabaseUrl.replace(/\/+$/, '') + '/functions/v1/track-pageview'
      : undefined);
  if (!endpoint) {
    throw new Error('@pc/analytics-client: pass either `endpoint` or `supabaseUrl` to initAnalytics()');
  }
  if (!opts.anonKey) {
    throw new Error('@pc/analytics-client: `anonKey` is required');
  }
  state = {
    app: opts.app,
    endpoint,
    anonKey: opts.anonKey,
    getSession: opts.getSession,
    debounceMs: opts.debounceMs ?? 500,
    lastFiredAt: 0,
    lastFiredKey: '',
  };
  return {
    trackPageview: (o) => trackPageview(o),
  };
}

async function resolveAuthHeader(): Promise<string | null> {
  if (!state?.getSession) return null;
  try {
    const result = await state.getSession();
    if (result && typeof result.access_token === 'string' && result.access_token.length > 0) {
      return result.access_token;
    }
  } catch {
    // Anonymous: degrade gracefully.
  }
  return null;
}

export function trackPageview(opts: TrackPageviewOpts = {}): void {
  if (!state || !isBrowser()) return;

  const path = window.location.pathname || '/';
  if (shouldSuppressTracking(path)) return;

  const now = Date.now();
  // Debounce against StrictMode double-effects and rapid SPA route
  // swaps. The dedupe key is path + template so navigating from /a to
  // /b inside the debounce window still fires both events.
  const key = path + '|' + (opts.path_template ?? '');
  if (
    now - state.lastFiredAt < state.debounceMs &&
    state.lastFiredKey === key
  ) {
    return;
  }
  state.lastFiredAt = now;
  state.lastFiredKey = key;

  const sessionId = getOrCreateSessionId();
  const referrer = document.referrer || '';
  const utm = parseUtm(window.location.search);
  const lang = (navigator.language || 'en').slice(0, 8);
  const viewport = {
    w: Math.max(1, Math.min(window.innerWidth || 0, 16384)),
    h: Math.max(1, Math.min(window.innerHeight || 0, 16384)),
  };
  const path_template = opts.path_template ?? normalizePath(path);

  const body = {
    app: state.app,
    path,
    path_template,
    vendor_slug: opts.vendor_slug,
    product_id: opts.product_id,
    referrer,
    utm,
    viewport,
    lang,
    session_id: sessionId,
  };

  void postPageview(state.endpoint, state.anonKey, body);
}

async function postPageview(
  endpoint: string,
  anonKey: string,
  body: Record<string, unknown>,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: anonKey,
    Authorization: 'Bearer ' + anonKey,
  };
  // Stamp a session-scoped JWT when the host app provides one so the
  // ingest function can read user_id / org_id off it. Failure to
  // resolve degrades to anonymous (the anon key bearer is fine).
  const token = await resolveAuthHeader();
  if (token) headers.Authorization = 'Bearer ' + token;
  try {
    await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    });
  } catch {
    // Network errors are not actionable from the client and the page
    // render is the contract. Swallow.
  }
}

// ---------------------------------------------------------------------------
// Vanilla installers — for non-React hosts.
// ---------------------------------------------------------------------------

export interface InstallRouteTrackerVanillaOpts {
  /** Fire one event right now on install. Default: true. */
  fireImmediately?: boolean;
  /**
   * One of:
   *   - 'astro'    -> listen to document `astro:page-load` + `astro:after-swap`
   *   - 'static'   -> single fire (DOMContentLoaded / immediate)
   *   - 'observe'  -> MutationObserver on <title> for static-HTML apps that
   *                   still do client-side route swaps without Astro.
   * Default: 'astro' when astro:page-load is available, else 'static'.
   */
  mode?: 'astro' | 'static' | 'observe';
}

/**
 * Wire a vanilla page-change listener using whatever mechanism the host
 * environment ships. Astro fires `astro:page-load` per navigation,
 * static-HTML apps just need a one-shot, and the observe mode falls
 * back to watching the document title.
 */
export function installRouteTrackerVanilla(
  client: AnalyticsClient,
  opts: InstallRouteTrackerVanillaOpts = {},
): () => void {
  if (!isBrowser()) return () => {};

  const fireImmediately = opts.fireImmediately ?? true;
  const mode = opts.mode ?? ('document' in globalThis && 'astro:page-load' in document ? 'astro' : 'static');

  const fire = () => client.trackPageview();
  const cleanups: Array<() => void> = [];

  if (fireImmediately) {
    if (document.readyState === 'loading') {
      const handler = () => fire();
      document.addEventListener('DOMContentLoaded', handler, { once: true });
      cleanups.push(() => document.removeEventListener('DOMContentLoaded', handler));
    } else {
      // Defer one microtask so the host caller's init code finishes
      // before we POST.
      queueMicrotask(fire);
    }
  }

  if (mode === 'astro') {
    const handler = () => fire();
    document.addEventListener('astro:page-load', handler);
    document.addEventListener('astro:after-swap', handler);
    cleanups.push(() => {
      document.removeEventListener('astro:page-load', handler);
      document.removeEventListener('astro:after-swap', handler);
    });
  } else if (mode === 'observe') {
    const titleEl = document.querySelector('title');
    if (titleEl) {
      const observer = new MutationObserver(() => fire());
      observer.observe(titleEl, { childList: true });
      cleanups.push(() => observer.disconnect());
    }
  }

  return () => {
    for (const c of cleanups) c();
  };
}

// ---------------------------------------------------------------------------
// Testing helpers — not part of the public surface, but exported so the
// vitest suite can exercise the debounce + suppression logic without
// faking sessionStorage.
// ---------------------------------------------------------------------------

/** @internal */
export function __resetForTests(): void {
  state = null;
}
